import React, { useEffect, useMemo } from 'react';
import { useWixPatternsContainer } from '@wix/bex-core/react';
import { observer } from 'mobx-react-lite';
import { FilterProps } from '../../model';
import { DateRangePicker } from '../DateRangePicker';
import { Box, Collapse, DatePickerProps, RadioGroup } from '@wix/design-system';
import {
  RangeItem,
  DateRangeFilterMode,
  DateRangePresetOptions,
  DateRangePredefinedPresetOptions,
  DateRangeFilterPresetsOverrideOptions,
  DateRangePreset,
  DefaultDateRangePreset,
  MAP_DATE_PRESET_TO_KEY,
  renderPresetToolbarTag,
} from '@wix/bex-core';
import { useDateRangeFilterState } from '../../hooks';

export interface DateRangeFilterProps
  extends FilterProps<RangeItem<Date>>,
    Partial<DatePickerProps> {
  /**
   * There are 3 possible modes:
   * - `ONLY_PREDEFINED`: show only predefined dates options
   * - `ONLY_CUSTOM`: show only custom date pickers
   * - `COMBINE`: default, combines both predefined and custom
   * @external
   */
  mode?: DateRangeFilterMode;
  /**
   * An optional array of 3 possible options: `'SEVEN_DAYS', 'FOURTEEN_DAYS', 'MONTH'`.
   * @default `['SEVEN_DAYS', 'FOURTEEN_DAYS', 'MONTH']`
   * @external
   */
  presetsOverride?: DateRangeFilterPresetsOverrideOptions;

  /**
   * If to include time resolution
   * @external
   */
  filterByTime?: boolean;
}

function _DateRangeFilter(props: DateRangeFilterProps) {
  const {
    filter,
    filterByTime,
    mode = 'COMBINE',
    presetsOverride,
    ...rest
  } = props;

  const { translate: t } = useWixPatternsContainer();
  const state = useDateRangeFilterState({
    filter,
    filterByTime,
  });

  const dateRangePreset: Partial<Record<DateRangePresetOptions, string>> =
    useMemo(() => {
      if (!presetsOverride) {
        return DefaultDateRangePreset;
      }
      const dateRangeOptionsArr = Object.keys(DateRangePreset).filter((key) =>
        presetsOverride.includes(key as DateRangePredefinedPresetOptions),
      );
      dateRangeOptionsArr.unshift(DateRangePreset.NONE);
      if (mode === 'COMBINE') {
        dateRangeOptionsArr.push(DateRangePreset.CUSTOM);
      }
      const dateRangeOptions = dateRangeOptionsArr.reduce(
        (cur, key) => ({
          ...cur,
          [key]: DateRangePreset[key as DateRangePresetOptions],
        }),
        {},
      ) as Record<DateRangePresetOptions, string>;

      return dateRangeOptions;
    }, [mode, presetsOverride]);

  useEffect(() => state.init(), []);

  const customElement = (
    <DateRangePicker
      dataHook="date-range-picker"
      state={state.dateRangePicker}
      {...rest}
    />
  );
  const innerComponent =
    mode === 'ONLY_CUSTOM' ? (
      customElement
    ) : (
      <Box direction="vertical">
        <RadioGroup
          value={state.dateRangePicker.selectedPreset}
          onChange={(v) =>
            v && state.dateRangePicker.onPresetChanged(v.toString())
          }
          dataHook="date-range-filter-group"
        >
          {Object.values(dateRangePreset).map((preset) =>
            preset === dateRangePreset.CUSTOM ? null : (
              <RadioGroup.Radio
                key={preset}
                value={preset}
                dataHook={`date-range-filter-option-${preset}`}
              >
                {t(MAP_DATE_PRESET_TO_KEY[preset])}
              </RadioGroup.Radio>
            ),
          )}
          {mode === 'COMBINE' && dateRangePreset.CUSTOM && (
            <RadioGroup.Radio
              value={dateRangePreset.CUSTOM}
              dataHook={`date-range-filter-option-${dateRangePreset.CUSTOM}`}
            >
              {t(MAP_DATE_PRESET_TO_KEY[dateRangePreset.CUSTOM])}
            </RadioGroup.Radio>
          )}
        </RadioGroup>
        <Collapse
          open={state.dateRangePicker.selectedPreset === dateRangePreset.CUSTOM}
        >
          <Box marginTop="14px" overflowY="visible">
            {customElement}
          </Box>
        </Collapse>
      </Box>
    );

  return (
    <Box dataHook={`date-range-filter-${filter.name}`} direction="vertical">
      {innerComponent}
    </Box>
  );
}
export const DateRangeFilter = observer(
  _DateRangeFilter,
) as typeof _DateRangeFilter & {
  displayName?: string;
  renderPresetToolbarTag?: typeof renderPresetToolbarTag;
};

DateRangeFilter.displayName = 'DateRangeFilter';
DateRangeFilter.renderPresetToolbarTag = renderPresetToolbarTag;
